// BUILD-046 task 8.20 (supporting model): which views the My Work host offers (design "Frontend Design", "Views and shell"; Req 1.3, 1.11, 7.1,
// 7.2, 7.3, 7.6, 12.18).
//
// `MyWorkPage` stays the host. With the new routes enabled and the Caller summary loaded, the two-way toggle becomes a three-way switcher:
//   My Work       (always)
//   Group / Shared (everyone: the unclaimed queue the Caller can take work from, and "Requests I issued")
//   Team Work     (only when `GET /work/me` reports `canOversee`: it is the Oversight_View)
// An admin-group or PM-role membership (the legacy `isManager` gate) grants nothing here: on the enabled routes the Team Work tab follows the
// server's `canOversee` only (Req 14.21, Decision D-J). While the Caller summary has not answered there is NO switcher (so nothing flickers in
// and out); when it FAILED the page is the legacy page exactly: the existing two-way toggle for the existing manager gate and the legacy Team
// Work list (the routes-disabled case, Req 1.3, 1.11).
//
// The Assign task button needs the Caller (it preselects them as the only recipient), so it appears only once the summary is ready. The
// simulated-identity banner is the banner model's: it shows when `me.simulated`, or in the local pilot even when the summary failed.
//
// Pure module: no React, no I/O, no runtime imports. Erasable TypeScript only.

import type { HostMeState } from './hostMeController';

export type HostView = 'my-work' | 'group-shared' | 'team-work';

/** `pending`: the summary has not answered. `legacy`: it failed (routes disabled): the legacy page. `shared`: it is ready. */
export type HostMode = 'pending' | 'legacy' | 'shared';

/** Which Team Work content the tab shows: the Oversight_View (server-scoped), or the unchanged legacy list. */
export type TeamKind = 'oversight' | 'legacy';

export interface HostTab {
  key: HostView;
  label: string;
}

export interface HostViews {
  mode: HostMode;
  tabs: readonly HostTab[];
  /** The switcher is shown only when there is more than one view to switch between. */
  showSwitcher: boolean;
  teamKind: TeamKind | null;
  /** The Assign task button (needs the Caller). */
  assignButton: boolean;
}

const MY_WORK: HostTab = { key: 'my-work', label: 'My Work' };
const GROUP_SHARED: HostTab = { key: 'group-shared', label: 'Group / Shared' };
const TEAM_WORK: HostTab = { key: 'team-work', label: 'Team Work' };

export function describeHostViews(meState: Pick<HostMeState, 'phase' | 'me'>, legacyIsManager: boolean): HostViews {
  if (meState.phase === 'ready' && meState.me !== null) {
    const oversee = meState.me.canOversee === true;
    const tabs = oversee ? [MY_WORK, GROUP_SHARED, TEAM_WORK] : [MY_WORK, GROUP_SHARED];
    return { mode: 'shared', tabs, showSwitcher: true, teamKind: oversee ? 'oversight' : null, assignButton: true };
  }
  if (meState.phase === 'error') {
    const tabs = legacyIsManager ? [MY_WORK, TEAM_WORK] : [MY_WORK];
    return { mode: 'legacy', tabs, showSwitcher: legacyIsManager, teamKind: legacyIsManager ? 'legacy' : null, assignButton: false };
  }
  return { mode: 'pending', tabs: [MY_WORK], showSwitcher: false, teamKind: null, assignButton: false };
}

/** The view to show: the selected one if it is still offered (a Caller change may remove Team Work), else My Work. */
export function resolveView(selected: HostView, views: Pick<HostViews, 'tabs'>): HostView {
  return views.tabs.some((tab) => tab.key === selected) ? selected : 'my-work';
}
